import { useForm, useStore } from '@tanstack/react-form';
import { defineMessages, useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { Layout } from '~/packages/eds/src/components/Layout/Layout';
import { Modal } from '~/packages/eds/src/components/Modal/Modal';
import { Text } from '~/packages/eds/src/components/Text/Text';

import { submitAppCrashFeedback } from './submitAppCrashFeedback';

const messages = defineMessages({
  title: {
    id: 'AppCrashFeedback.title',
    defaultMessage: 'Tell Us What Happened',
    description: 'Title of the modal for reporting details about a crash.',
  },
  description: {
    id: 'AppCrashFeedback.description',
    defaultMessage:
      'Tell us what you were doing when the page stopped working. Anything you add helps us track down the cause.',
    description: 'Description shown above the crash report form.',
  },
  detailsLabel: {
    id: 'AppCrashFeedback.detailsLabel',
    defaultMessage: 'What happened?',
    description: 'Label for the crash report details field.',
  },
  detailsPlaceholder: {
    id: 'AppCrashFeedback.detailsPlaceholder',
    defaultMessage: 'Describe the steps that led to the error',
    description: 'Placeholder for the crash report details field.',
  },
  detailsRequired: {
    id: 'AppCrashFeedback.detailsRequired',
    defaultMessage: 'Add a few details before sending.',
    description:
      'Validation message when the crash report details field is empty.',
  },
  note: {
    id: 'AppCrashFeedback.note',
    defaultMessage: 'Your report is attached to the error we already logged.',
    description:
      'Reassurance shown when the crash report can be linked to the logged error.',
  },
  cancel: {
    id: 'AppCrashFeedback.cancel',
    defaultMessage: 'Cancel',
    description: 'Action that dismisses the crash report modal.',
  },
  submit: {
    id: 'AppCrashFeedback.submit',
    defaultMessage: 'Send Details',
    description: 'Primary action that sends the crash report.',
  },
});

interface AppCrashFeedbackModalProps {
  isVisible: boolean;
  onHide: () => void;
  /** Confirms the send, since the crash screen has no toast container left. */
  onSent: () => void;
  eventId?: string;
}

export function AppCrashFeedbackModal({
  isVisible,
  onHide,
  onSent,
  eventId,
}: AppCrashFeedbackModalProps) {
  const intl = useIntl();

  const form = useForm({
    defaultValues: { details: '' },
    onSubmit: async ({ value }) => {
      submitAppCrashFeedback({ message: value.details.trim(), eventId });
      onSent();
      onHide();
    },
  });

  useFormFocusManager(form);

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );

  const cancelLabel = intl.formatMessage(messages.cancel);
  const submitLabel = intl.formatMessage(messages.submit);

  return (
    <Modal
      isVisible={isVisible}
      header={{
        title: intl.formatMessage(messages.title),
      }}
      footer={{
        actions: [
          {
            label: cancelLabel,
            text: cancelLabel,
            variant: 'tertiary',
            onClick: onHide,
          },
          {
            disabled: isSubmitting,
            icon: { key: 'send', position: 'left' },
            isLoading: isSubmitting,
            label: submitLabel,
            text: submitLabel,
            variant: 'primary',
            onClick: () => {
              form.handleSubmit().catch(() => {});
            },
          },
        ],
      }}
      onHide={onHide}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit().catch(() => {});
        }}
      >
        <Layout direction="column" spacing="l">
          {/* Body copy rather than the header's `subtitle`, which EDS truncates
              to a single line. */}
          <Text as="p" tone="subdued" variant="body-m">
            {intl.formatMessage(messages.description)}
          </Text>
          <form.Field
            name="details"
            validators={{
              onSubmit: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.detailsRequired),
              onChange: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.detailsRequired),
            }}
          >
            {(field) => (
              <FormField<string, false>
                input={TextArea}
                label={intl.formatMessage(messages.detailsLabel)}
                required
                name={field.name}
                placeholder={intl.formatMessage(messages.detailsPlaceholder)}
                value={field.state.value}
                error={
                  submissionAttempts > 0
                    ? (field.state.meta.errors[0] ?? undefined)
                    : undefined
                }
                inputProps={{ enableAutoResize: true, rows: 4 }}
                onChange={(value) => field.handleChange(value ?? '')}
              />
            )}
          </form.Field>
          {eventId && (
            <Text as="p" tone="subdued" variant="body-s">
              {intl.formatMessage(messages.note)}
            </Text>
          )}
        </Layout>
      </form>
    </Modal>
  );
}
